import ToggleSwitch from '@/components/ToggleSwitch';
import { useCreateOptionChoice, useUpdateProductOption } from '@/features/menu/hooks/useMenu';
import { loc } from '@/utils/localized';
import { fail, router } from '@/features/menu/utils/product-page-actions';
import {
  activeChoiceCount,
  defaultLimitOn,
  hasLimit,
  legacyIssues,
  limitOffBody,
  limitRange,
  parseLimitInput,
  stockShortfall,
  summarizeSelection,
  type LimitField,
} from '@/features/menu/utils/option-limits';
import { inputErrorText, legacyText, summaryText } from '@/features/menu/utils/option-limits-text';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import type { Product, ProductOption } from '@restaurant/api-contracts';
import { BlockFooter, BlockTitle, f7, List, ListItem } from 'framework7-react';

export default function OptionEditContent({
  product,
  option,
  menuId,
  currency,
}: {
  product: Product;
  option: ProductOption;
  menuId: string;
  currency: string;
}) {
  const t = useT();
  const updateOption = useUpdateProductOption();
  const createChoice = useCreateOptionChoice();

  const editName = () => {
    f7.dialog.prompt(
      t.menu.productForm.name,
      t.menu.productForm.editName,
      (value) => {
        const text = value.trim();
        if (!text) return;
        updateOption.mutate(
          { optionId: option.id, body: { name: { en: text } } },
          { onError: fail },
        );
      },
      undefined,
      loc(option.name),
    );
  };

  const lt = t.menu.optionLimits;
  const choiceCount = activeChoiceCount(option);
  const limited = hasLimit(option);
  const issues = legacyIssues(option);
  const shortfall = stockShortfall(option);

  const togglePickSeveral = (on: boolean) => {
    const body = on
      ? {
          isMultiple: true,
          maxSelect: null,
          minSelect: Math.max(option.minSelect, option.isRequired ? 1 : 0),
          isRequired: option.isRequired || option.minSelect >= 1,
        }
      : {
          isMultiple: false,
          maxSelect: 1,
          minSelect: option.isRequired ? 1 : 0,
          isRequired: option.isRequired,
        };
    updateOption.mutate({ optionId: option.id, body }, { onError: fail });
  };

  const toggleLimit = (on: boolean) => {
    const { limits, isRequired } = on ? defaultLimitOn(option) : limitOffBody();
    updateOption.mutate(
      { optionId: option.id, body: { ...limits, isRequired } },
      { onError: fail },
    );
  };

  const editLimit = (field: LimitField) => {
    const { lo, hi } = limitRange(field, choiceCount);
    const prompt = field === 'min' ? lt.minPrompt(lo, hi) : lt.maxPrompt(lo, hi);
    const title = field === 'min' ? lt.minimum : lt.maximum;
    const current = field === 'min' ? option.minSelect : option.maxSelect;
    f7.dialog.prompt(
      prompt,
      title,
      (value) => {
        const result = parseLimitInput(field, value, option, choiceCount);
        if (!result.ok) {
          f7.dialog.alert(inputErrorText(result.error, field, lt), lt.notSaved);
          return;
        }
        updateOption.mutate(
          { optionId: option.id, body: { ...result.limits, isRequired: result.isRequired } },
          { onError: fail },
        );
      },
      undefined,
      current === null ? '' : String(current),
    );
  };

  const choices = option.choices.slice().sort((a, b) => a.sortOrder - b.sortOrder);

  const addChoice = async () => {
    try {
      const created = await createChoice.mutateAsync({
        optionId: option.id,
        body: { name: { en: t.menu.newChoice }, price: 0 },
      });
      router()?.navigate(
        `/menu/${menuId}/product/${product.id}/option/${option.id}/choice/${created.id}`,
      );
    } catch (e) {
      fail(e);
    }
  };

  return (
    <>
      <BlockTitle>{t.menu.productForm.general}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListItem
          title={t.menu.productForm.name}
          after={loc(option.name)}
          link="#"
          onClick={editName}
        />
        <ListItem title={lt.pickSeveral}>
          <ToggleSwitch checked={option.isMultiple} onChange={togglePickSeveral} />
        </ListItem>
        {option.isMultiple && (
          <ListItem title={lt.limitHowMany}>
            <ToggleSwitch checked={limited} onChange={toggleLimit} />
          </ListItem>
        )}
        {option.isMultiple && limited && (
          <ListItem
            title={lt.minimum}
            after={String(option.minSelect)}
            link="#"
            onClick={() => editLimit('min')}
          />
        )}
        {option.isMultiple && limited && (
          <ListItem
            title={lt.maximum}
            after={option.maxSelect === null ? '-' : String(option.maxSelect)}
            link="#"
            onClick={() => editLimit('max')}
          />
        )}
      </List>
      <BlockFooter>{summaryText(summarizeSelection(option), lt)}</BlockFooter>
      {issues.map((issue) => (
        <BlockFooter key={issue.kind} className="text-color-red">
          {legacyText(issue, lt)}
        </BlockFooter>
      ))}
      {shortfall && (
        <BlockFooter className="text-color-red">
          {lt.stockWarning(shortfall.inStock, shortfall.total)}
        </BlockFooter>
      )}

      <BlockTitle>{t.menu.addChoice}</BlockTitle>
      {choices.length > 0 ? (
        <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
          {choices.map((c) => (
            <ListItem
              key={c.id}
              title={loc(c.name)}
              after={formatMoney(c.price, currency)}
              link={`/menu/${menuId}/product/${product.id}/option/${option.id}/choice/${c.id}`}
              {...(!c.inStock ? { badge: t.menu.outOfStockChip, badgeColor: 'gray' } : {})}
            />
          ))}
        </List>
      ) : (
        <BlockFooter>{t.menu.noChoicesYet}</BlockFooter>
      )}

      <div className="bulk-add-card">
        <div
          className="bulk-add-addrow-btn bulk-add-addrow-row"
          role="button"
          tabIndex={0}
          onClick={addChoice}
        >
          <button
            type="button"
            className="bulk-add-plus"
            aria-label={t.menu.addChoice}
            tabIndex={-1}
          />
          <span>{t.menu.addChoice}</span>
        </div>
      </div>

      <BlockFooter>{t.menu.optionEditHint}</BlockFooter>
    </>
  );
}
